/** @format */

import { useState, useEffect, useRef } from 'react'
import useGlobalData from 'src/hooks/useGlobalData'
import { RegionStateType } from 'src/store/globalDataSlice'
import { useLocation, useHistory } from 'react-router'
import { replaceSearchParams } from 'src/utils'

interface NavRegionProps {
  className?: string
  defaultRegion?: string
  /** 为 false 时不写 localStorage.systemId（仍会同步 URL） */
  isUpdate?: boolean
  effect?: any[]
  filterResult?: (value: any[]) => any[]
  onChange?: (value: string) => void
}

/**
 * 初始化优先级：
 * query_region > defaultRegion > localStorage.systemId > regionList[0].regionKey
 */
const resolveRegionByPriority = (
  queryRegion?: string | null,
  defaultRegion?: string,
  listFirstKey?: string
) => queryRegion || defaultRegion || localStorage.getItem('systemId') || listFirstKey || ''

const isSameRegionList = (prev: RegionStateType['list'], next: RegionStateType['list']) =>
  prev.length === next.length &&
  prev.every((item, index) => item.regionKey === next[index]?.regionKey)

const useNavRegion = (config?: NavRegionProps) => {
  const history = useHistory()
  const location = useLocation()
  const locationRef = useRef(location)
  locationRef.current = location

  const { isUpdate = true, defaultRegion, filterResult, onChange, effect } = config || {}
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const queryRegion = new URLSearchParams(location.search).get('region') || ''

  const [region, setRegion] = useState(() => {
    const initial = resolveRegionByPriority(queryRegion, defaultRegion)
    // 首屏同步写 systemId，避免子组件首批 request 读到旧值
    if (initial && isUpdate) {
      localStorage.setItem('systemId', initial)
    }
    return initial
  })
  const regionRef = useRef(region)
  regionRef.current = region

  const [regionList, setRegionList] = useState<RegionStateType['list']>([])
  const gloabalRegionList = useGlobalData('monitorRegionList') as RegionStateType['list']
  const extraEffects = effect || []

  const updateRegion = (next: string) => {
    if (!next || next === regionRef.current) {
      return
    }
    regionRef.current = next
    if (isUpdate) {
      localStorage.setItem('systemId', next)
    }
    setRegion(next)
    onChangeRef.current?.(next)
  }

  // region 变化时：同步 URL 与 localStorage
  useEffect(() => {
    if (!region) {
      return
    }
    if (isUpdate) {
      localStorage.setItem('systemId', region)
    }
    replaceSearchParams(history, locationRef.current, { region })
  }, [region, isUpdate, history])

  // regionList 就绪后，按优先级补齐 / 校正 region
  useEffect(() => {
    if (!gloabalRegionList?.length) {
      return
    }
    const list = filterResult ? filterResult(gloabalRegionList) : gloabalRegionList
    setRegionList(prev => (isSameRegionList(prev, list) ? prev : list))
    // filterResult 由调用方传入，不放入 deps，避免内联函数反复重置列表
  }, [gloabalRegionList, defaultRegion])

  // 绑定控制台 region 选择器
  useEffect(() => {
    if (!regionList?.length) {
      return
    }

    const initialRegion = resolveRegionByPriority(
      new URLSearchParams(locationRef.current.search).get('region'),
      defaultRegion,
      regionList[0]?.regionKey
    )
    // 列表就绪后主动对齐一次，避免只依赖 initRegion 回调导致空→有值二次请求
    updateRegion(initialRegion)

    const windowRegion = window['__ksStorage__'].initRegion(
      regionList.map(item => ({
        ...item,
        regionCode: item.regionKey
      })),
      initialRegion
    )
    window['__ksStorage__'].on('region', (data: string) => {
      updateRegion(data)
    })

    return () => {
      windowRegion?.remove?.()
    }
  }, [regionList, defaultRegion, ...extraEffects])

  return region
}

export default useNavRegion
